import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, CENTROS, OPTOS, OPC, today } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { TextField, SelectField, DateField } from "@/components/Fields";
import { RemitentesList } from "@/components/RemitentesList";

const empty = (centro) => ({ nombre: "", codigo: "", fecha_nacimiento: null, centro: centro || "", optometrista: "", terapeuta: "", quien_remite: "", telefono: "", email: "", fecha_llamada: today(), estado_cita: "CITA DADA", fecha_cita: null });

const Duplicados = ({ dups, onOpen }) => (
  <div data-testid="np-duplicados" className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
    <div className="flex items-center gap-2 font-semibold text-amber-800 mb-1"><AlertTriangle className="h-4 w-4" />Posible paciente duplicado</div>
    {dups.map((d) => (
      <Link key={d.id} to={`/pacientes/${d.id}`} onClick={onOpen} data-testid={`np-dup-${d.id}`} className="flex justify-between gap-2 py-1 text-slate-800 hover:underline">
        <span className="truncate">{d.nombre}{d.centro ? ` · ${d.centro}` : ""}{d.telefono ? ` · ${d.telefono}` : ""}</span>
        <span className="text-[11px] text-amber-700 whitespace-nowrap">coincide {d.motivos.join(", ")}</span>
      </Link>
    ))}
    <p className="text-[11px] text-amber-700 mt-1">Pulsa en uno para abrir su ficha, o crea el paciente igualmente si es otra persona.</p>
  </div>
);

export const NewPatientDialog = () => {
  const { centro } = useCentro();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty(centro));
  const nav = useNavigate();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [dups, setDups] = useState([]);
  useEffect(() => {
    if (!open || (f.nombre.trim().length < 4 && f.telefono.replace(/\D/g, "").length < 9 && !f.email.trim())) { setDups([]); return; }
    const h = setTimeout(() => {
      api.get("/patients/duplicados", { params: { nombre: f.nombre, telefono: f.telefono, email: f.email } }).then((r) => setDups(r.data));
    }, 400);
    return () => clearTimeout(h);
  }, [open, f.nombre, f.telefono, f.email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nombre.trim()) return toast.error("El nombre es obligatorio");
    if (dups.length && !window.confirm(`Hay ${dups.length} paciente(s) parecido(s): ${dups.map((d) => d.nombre).join(", ")}. ¿Crear igualmente?`)) return;
    const { data } = await api.post("/patients", { ...f, nombre: f.nombre.trim().toUpperCase() });
    toast.success("Paciente creado");
    setOpen(false);
    setF(empty(centro));
    nav(`/pacientes/${data.patient.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(empty(centro)); }}>
      <DialogTrigger asChild>
        <Button data-testid="new-patient-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-9">
          <Plus className="h-4 w-4 mr-1" /> Nuevo paciente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="new-patient-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Nuevo paciente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><TextField label="Nombre y apellidos" value={f.nombre} onChange={set("nombre")} testId="np-nombre" autoFocus /></div>
          <TextField label="Código" value={f.codigo} onChange={set("codigo")} testId="np-codigo" />
          <DateField label="Fecha de nacimiento" value={f.fecha_nacimiento} onChange={set("fecha_nacimiento")} testId="np-fecha-nacimiento" />
          <SelectField label="Centro" value={f.centro} onChange={set("centro")} options={CENTROS} testId="np-centro" />
          <SelectField label="Optometrista" value={f.optometrista} onChange={set("optometrista")} options={OPTOS} testId="np-opto" />
          <SelectField label="Terapeuta" value={f.terapeuta} onChange={set("terapeuta")} options={OPTOS} testId="np-terapeuta" />
          <TextField label="Quién remite" value={f.quien_remite} onChange={set("quien_remite")} list="remite-list" testId="np-remite" />
          <TextField label="Teléfono" value={f.telefono} onChange={set("telefono")} testId="np-telefono" />
          <TextField label="Email" value={f.email} onChange={set("email")} testId="np-email" />
          <DateField label="Fecha llamada" value={f.fecha_llamada} onChange={set("fecha_llamada")} testId="np-fecha-llamada" />
          <SelectField label="¿Coge cita?" value={f.estado_cita} onChange={set("estado_cita")} options={OPC.estado_cita} testId="np-estado-cita" />
          {f.estado_cita === "CITA DADA" && <DateField label="Fecha cita" value={f.fecha_cita} onChange={set("fecha_cita")} testId="np-fecha-cita" />}
          <RemitentesList id="remite-list" />
          {dups.length > 0 && <Duplicados dups={dups} onOpen={() => setOpen(false)} />}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="np-cancel">Cancelar</Button>
            <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="np-submit">{dups.length ? "Crear igualmente" : "Crear y abrir ficha"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
